import { type ReactNode, useMemo } from 'react';

import { Icon } from '@flintmint/web-shared/components/icon';

import { type ContextItemProps } from '@/components/context-item';
import { SourceListLayout } from '../source-list-layout';
import { getSourceId, useSourceListModel } from '@/hooks/use-source-list-model';
import { SourceRow, isLocalSourceRowItem, type LocalSourceRowItem, type SourceRowItem } from '@/components/source-row';
import type { UserSourceAvailabilityScope } from '@flintmint/core-shared/rxdb';

import styles from '@/components/ai-sources-section/ai-context-sources-section.module.scss';

export const ManagedSourcesGrid = ({
  ids = [],
  onDetach,
  tabId,
  localItems = [],
  toggleAttachment,
  onRemoveLocalItem,
  onOpenAudioPreview,
  openAddContextItems,
  noAttachedFilesView,
  noteUserSources = {},
  renderLocalItems = false,
  onUserSourceEnabledChange,
  getSharedContextItemProps,
  showAllUserSources = false,
  showNoAttachedState = false,
  resetSearchKey,
}: ManagedSourcesGridProps) => {
  const { searchTerm, setSearchTerm, sortedUserSources, isLoading } = useSourceListModel({
    ids,
    showAllUserSources,
    noteUserSources,
    resetSearchWhenNoAttachedState: showNoAttachedState,
    resetSearchKey,
  });
  const isAttachedTab = tabId === 'on-this-note';
  const isLibraryTab = tabId === 'from-source-library';

  const listRows = useMemo<SourceRowItem[]>(
    () => (renderLocalItems ? [...localItems, ...sortedUserSources] : sortedUserSources),
    [renderLocalItems, localItems, sortedUserSources]
  );

  return (
    <>
      <SourceListLayout<SourceRowItem>
        lanes={2}
        laneGap={8}
        data={listRows}
        defaultSize={122}
        paddingStart={12}
        loading={isLoading}
        resetKey={searchTerm}
        addActionMode="button"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        noAttachedView={noAttachedFilesView}
        onAddContextItems={openAddContextItems}
        showNoAttachedState={showNoAttachedState}
        getItemKey={getSourceRowItemKey}
        paddingEnd={listRows.length > 2 ? 72 : 0}
        noResultsConfig={{
          className: styles.noData,
          icon: <Icon type="search-01-bulk" size={60} multiColor />,
          getDescription: (term) =>
            term ? `No Source Files\n found for your request` : `Create Sources for AI\n to consider as knowledge source`,
        }}
        rowRenderer={(item) => (
          <div className={styles.contextItemRow}>
            <SourceRow
              item={item}
              onDetach={onDetach}
              showSourceInputMeta
              noteUserSources={noteUserSources}
              toggleAttachment={toggleAttachment}
              onRemoveLocalItem={onRemoveLocalItem}
              onOpenAudioPreview={onOpenAudioPreview}
              canToggleEnabled={isAttachedTab}
              canToggleAttachment={isLibraryTab}
              onUserSourceEnabledChange={onUserSourceEnabledChange}
              getSharedContextItemProps={getSharedContextItemProps}
              highlightAttached={isLibraryTab}
            />
          </div>
        )}
      />
    </>
  );
};

const getSourceRowItemKey = (item: SourceRowItem) => (isLocalSourceRowItem(item) ? item.id : getSourceId(item));

interface ManagedSourcesGridProps {
  tabId?: 'on-this-note' | 'from-source-library';
  showAllUserSources?: boolean;
  ids?: string[];
  noteUserSources?: Record<string, { index?: number; enabled?: boolean }>;
  onDetach?: (id: string) => void;
  toggleAttachment?: (sourceId: string, attached: boolean) => void;
  onUserSourceEnabledChange?: (userSourceId: string, enabled: boolean) => void;
  onOpenAudioPreview?: (id: string) => void;
  getSharedContextItemProps?: (
    id: string,
    name: string,
    sourceType?: string | null,
    source?: string | null,
    uploading?: boolean,
    availabilityScope?: UserSourceAvailabilityScope
  ) => Partial<ContextItemProps>;
  openAddContextItems?: () => void;
  localItems?: LocalSourceRowItem[];
  onRemoveLocalItem?: (id: string) => void;
  renderLocalItems?: boolean;
  showNoAttachedState?: boolean;
  noAttachedFilesView?: ReactNode;
  resetSearchKey?: number;
}
